import posthog from "posthog-js";

// Project keys are public (they ship in the bundle either way). Switch on the
// runtime hostname, exactly like recaptcha-key.js, so prod gets the Production
// project and everything else (dev, local) gets Sandbox.
//
// This deliberately does NOT key off NODE_ENV: every deployed build is a
// production build, so the dev site shipped the Production key and the sandbox
// branch was dead-code-eliminated out of the bundle entirely.
const PROD_KEY = "phc_qVsGAxb5gtPreTKETktztzpZgmKtPrRmq4438eQQxLzH";
const SANDBOX_KEY = "phc_xRAXu64AeRmZHhVqk37gAKmDceDgikzsJkxqg7xRQXhr";

const isProdHost =
  typeof window !== "undefined" &&
  window.location.hostname === "public-tools.prod.aws.cinch.io";

const API_KEY =
  process.env.REACT_APP_POSTHOG_API_KEY ||
  (isProdHost ? PROD_KEY : SANDBOX_KEY);
const API_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// Mirrors the main app's check in user-state-context so a person keeps the
// same is_internal_user value on both sides of the signup -> app handoff.
// Only derivable once the visitor has typed an email, so events before the
// signup form is submitted (e.g. signup_page_viewed) stay unset.
export function isInternalEmail(email) {
  return !!email && email.trim().toLowerCase().endsWith("@cinch.io");
}

// Query-string keys we lift into PostHog super properties so every
// event in the session carries them. UTMs are the industry standard for
// marketing attribution; `feature_focus` is our own routing key for
// which product signup form the visitor landed on.
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

// Ad-platform click IDs. A Meta or Google click often carries one of these
// and no UTMs at all, so without them an untagged paid visit is
// indistinguishable from organic. Add a key here and it also needs the
// matching backend allowlist, serializer field and HubSpot property.
const CLICK_ID_KEYS = ["gclid", "fbclid"];

// Which platform a click ID implies, used only to stop an untagged paid
// click being filed as "direct".
const CLICK_ID_SOURCES = { gclid: "google", fbclid: "facebook" };

const ATTRIBUTION_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS, "feature_focus"];

const ATTRIBUTION_STORAGE_KEY = "cinch_signup_attribution_v1";

// Trim + lowercase + length-cap to keep PostHog property cardinality
// sane and stop someone from blasting absurdly long URLs through us.
function sanitizeAttribution(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase().slice(0, 64);
  return trimmed || null;
}

// Click IDs are opaque, case-sensitive and long — an fbclid runs well past
// 100 characters. Lowercasing or truncating one leaves a value that is stored
// but no longer matches anything on the ad platform, which is worse than not
// capturing it, so they get their own sanitizer.
function sanitizeClickId(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().slice(0, 512);
  return trimmed || null;
}

const sanitizerFor = (key) =>
  CLICK_ID_KEYS.includes(key) ? sanitizeClickId : sanitizeAttribution;

function readAttributionFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    let any = false;
    ATTRIBUTION_KEYS.forEach((key) => {
      const raw = params.get(key);
      const clean = sanitizerFor(key)(raw);
      if (clean) {
        found[key] = clean;
        any = true;
      }
    });
    return any ? found : null;
  } catch {
    return null;
  }
}

function readAttributionFromStorage() {
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeAttributionToStorage(values) {
  try {
    window.sessionStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(values),
    );
  } catch {
    // sessionStorage can throw in private modes or with strict privacy
    // extensions — silently fall through and rely on URL-only capture.
  }
}

// Last-touch attribution: if the current URL carries any of these
// params we overwrite whatever was stashed, so the most recent campaign
// (or product the visitor switched to) wins. Falls back to the stashed
// value when the URL is bare (e.g. user reloaded without the query).
export function resolveAttribution() {
  const fromUrl = readAttributionFromUrl();
  if (fromUrl) {
    writeAttributionToStorage(fromUrl);
    return fromUrl;
  }
  return readAttributionFromStorage();
}

// Spread-ready subset for outbound API payloads. Skips feature_focus
// (the URL slug owns that on the server) and omits absent keys instead
// of sending them as null. Returns {} when nothing is captured so the
// caller can `...getUtmPayload()` unconditionally.
export function getUtmPayload() {
  const attribution = resolveAttribution() || {};
  const payload = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    if (attribution[key]) payload[key] = attribution[key];
  });
  return payload;
}

// Area label shared with the main app's dashboards (CIN-6465). Both apps
// report into the same PostHog project, so this lets signup -> app show up
// in one breakdown. Every page here is public, so is_public_route is always
// true, and a visitor never moves between areas without a full page load.
const ROUTE_CLASSES = [
  ["/signup", "signup"],
  ["/preview-template", "preview-template"],
];

export function classifyRoute(pathname = "") {
  const match = ROUTE_CLASSES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : "unmapped";
}

if (API_KEY) {
  posthog.init(API_KEY, {
    api_host: API_HOST,
    capture_exceptions: true,
    // Non-prod only: send each capture immediately so we can verify events
    // hit the wire in the network tab. Production keeps batching on for
    // efficiency. Same gate also lets in headless-browser captures so we
    // can E2E-test the funnel; production still drops bot traffic.
    ...(!isProdHost && {
      request_batching: false,
      opt_out_useragent_filter: true,
    }),
  });

  const attribution = resolveAttribution() || {};
  if (!attribution.utm_source) {
    // An untagged ad click arrives with a click ID and no UTMs. Calling that
    // "direct" files paid acquisition as organic, so the click ID names the
    // source when it can; only a genuinely bare URL is direct.
    const clickIdKey = CLICK_ID_KEYS.find((key) => attribution[key]);
    attribution.utm_source = clickIdKey ? CLICK_ID_SOURCES[clickIdKey] : "direct";
  }
  posthog.register({
    ...attribution,
    product_area: "public_tools",
    route_class: classifyRoute(window.location.pathname),
    is_public_route: true,
  });
}

export default posthog;
